import { gql } from 'graphql-request'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

import { useQuery } from '@hooks/graphql'
import Spinner from '@components/Spinner'
import { useMutation } from '@tanstack/react-query'
import { client } from '@helpers/api'
import { useFlashMessage } from '@components/FlashMessage'

import NoResults from './NoResults'
import ProfileDetailsCard from './ProfileDetailsCard'
import JourneyToFirstImpactCard from './JourneyToFirstImpactCard'
import ClassroomsTable from './ClassroomsTable'
import ImpersonationSessionsList from './ImpersonationSessionsList'

const journeyStepsKeys = [
  'signedUp',
  'seriesCreated',
  'createdAndAssignedSeries',
  'enrolledStudents',
  'studentsSubmitted',
  'insightsGenerated'
];
const totalJourneySteps = journeyStepsKeys.length;

const getJourneyProgress = (checklist, educatorOnboardingCompleted) => {
  if (!checklist && typeof educatorOnboardingCompleted === 'undefined') return 0;
  let completedSteps = 0;
  journeyStepsKeys.forEach(key => {
    if (key === 'signedUp') {
      if (educatorOnboardingCompleted) {
        completedSteps++;
      }
    } else if (checklist && checklist[key]) {
      completedSteps++;
    }
  });
  return totalJourneySteps > 0 ? (completedSteps / totalJourneySteps) * 100 : 0;
};

const PROFILE_QUERY = gql`
  query adminProfile($id: ID!) {
    adminProfile(id: $id) {
      id
      fullName
      email
      phoneCountryCode
      phoneNumber
      educatorOnboardingCompleted
      adminActivationMilestones {
        seriesCreated
        tutorCreated
        enrolledStudents
        createdAndAssignedSeries
        studentsSubmitted
        insightsGenerated
      }
      organizationRole
      trialStartedAt
      trialLength
      allowSupportAccess
      user {
        id
        impersonationSessions {
          startedAt
          finishedAt
          admin {
            firstName
            lastName
          }
          adminAuditsDuringPeriod {
            id
            action
            auditableId
            auditableType
            auditedChanges
            createdAt
            user {
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`

const CLASSROOMS_QUERY = gql`
  query adminClassrooms($profileId: ID!, $organizationId: ID!, $page: Int, $perPage: Int) {
    adminClassrooms(profileId: $profileId, organizationId: $organizationId, page: $page, perPage: $perPage) {
      nodes {
        id
        name
        archived
        studentsCount
        pendingInvitationsCount
        seriesAssignments {
          nodes {
            series {
              id
              name
              educatorProjects {
                id
                name
              }
            }
          }
        }
      }
      pagesCount
      nodesCount
    }
  }
`

const EducatorDetails = ({ organizationId }) => {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setFlashMessage } = useFlashMessage()

  const page = parseInt(searchParams.get('page')) || 1
  const setPage = page => setSearchParams({ page: page.toString() })

  const { isLoading: isProfileLoading, data: { adminProfile: profile = {} } = {} } = useQuery({
    queryKey: ['adminProfile', id],
    gqlQuery: PROFILE_QUERY,
    variables: { id }
  })

  const { isLoading: isClassroomsLoading, data: adminClassroomsRawData } = useQuery({
    queryKey: ['adminClassrooms', id, organizationId, page],
    gqlQuery: CLASSROOMS_QUERY,
    variables: { profileId: id, organizationId, page, perPage: 5 }
  })

  const classrooms = adminClassroomsRawData?.adminClassrooms?.nodes || [];
  const pagesCount = adminClassroomsRawData?.adminClassrooms?.pagesCount;

  const { mutate: impersonateUser, isLoading: isImpersonating } = useMutation({
    mutationFn: async (userId) => await client.post('/admin/impersonations', { user_id: userId }),
    onSuccess: () => window.location.href = '/educators',
    onError: () => setFlashMessage('Something went wrong', 'error')
  })

  const handleImpersonateUser = (userId) => {
    if (userId) impersonateUser(userId)
  }

  const journeyProgressPercentage = getJourneyProgress(profile.adminActivationMilestones, profile.educatorOnboardingCompleted);

  if (isProfileLoading && !profile.id) return <Spinner />

  return (
    <div className='sm:px-5 pt-5 mb-5'>
      <Link to={`/admin/organizations/${organizationId}/educators`} className='flex items-center text-base mb-5 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'>
        <ArrowLeft className='size-5 mr-1' />
        Back to educators
      </Link>

      <ProfileDetailsCard
        profile={profile}
        isImpersonating={isImpersonating}
        handleImpersonateUser={handleImpersonateUser}
      />

      <JourneyToFirstImpactCard
        journeyProgressPercentage={journeyProgressPercentage}
        journeyStepsKeys={journeyStepsKeys}
        profile={profile}
      />

      <h3 className='text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100 mb-3'>Classrooms</h3>

      <Choose>
        <When condition={isClassroomsLoading && !adminClassroomsRawData}>
          <Spinner />
        </When>
        <When condition={!isClassroomsLoading && (!classrooms || classrooms.length === 0)}>
          <NoResults />
        </When>
        <Otherwise>
          <ClassroomsTable
            classrooms={classrooms}
            isLoading={isClassroomsLoading}
            pagesCount={pagesCount}
            page={page}
            setPage={setPage}
          />
        </Otherwise>
      </Choose>

      <h3 className='text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100 mb-3 mt-5'>Impersonation Sessions</h3>

      <ImpersonationSessionsList
        impersonationSessions={profile.user?.impersonationSessions || []}
        isLoading={isProfileLoading}
      />
    </div>
  )
}

export default EducatorDetails
